import { Text } from "react-native-paper";
import { useAppTheme } from "../useAppTheme";
import Box from "./Box";

type AppTheme = ReturnType<typeof useAppTheme>;

export type QuestionMapItemState =
	| "notVisited"
	| "current"
	| "answered"
	| "skipped"
	| "correct"
	| "incorrect"
	| "partial";

type QuestionMapItemProps = {
	number: number | string;
	state: QuestionMapItemState;
	size?: number;
};

const FILLED_STATES: ReadonlySet<QuestionMapItemState> = new Set([
	"current",
	"answered",
	"correct",
	"incorrect",
	"partial",
]);

const getDotColor = (state: QuestionMapItemState, theme: AppTheme) => {
	switch (state) {
		case "correct":
			return theme.palette.success.main;
		case "partial":
			return theme.palette.warning.main;
		case "incorrect":
			return theme.palette.error.main;
		default:
			return undefined;
	}
};

const formatLabel = (number: number | string) =>
	typeof number === "number" ? String(number).padStart(2, "0") : number;

const Dot = ({
	color,
	size,
	ringColor,
}: { color: string; size: number; ringColor: string }) => {
	const dotSize = size * 0.375;
	const offset = -size * 0.09375;
	return (
		<Box
			style={{
				position: "absolute",
				top: offset,
				right: offset,
				width: dotSize,
				height: dotSize,
				borderRadius: dotSize / 2,
				backgroundColor: color,
				borderWidth: 1,
				borderColor: ringColor,
			}}
		/>
	);
};

const Circle = ({
	state,
	number,
	size,
	theme,
}: {
	state: QuestionMapItemState;
	number: number | string;
	size: number;
	theme: AppTheme;
}) => {
	const isFilled = FILLED_STATES.has(state);
	const isDashed = state === "skipped";
	const dotColor = getDotColor(state, theme);
	const labelColor = isFilled
		? theme.colors.onPrimary
		: isDashed
			? theme.colors.primary
			: theme.colors.onSurface;

	return (
		<Box
			style={{
				width: size,
				height: size,
				borderRadius: size / 2,
				backgroundColor: isFilled
					? theme.colors.primary
					: theme.colors.onPrimary,
				borderWidth: isDashed ? 1.5 : isFilled ? 0 : 1,
				borderStyle: isDashed ? "dashed" : "solid",
				borderColor: isDashed
					? theme.colors.primary
					: theme.colors.primaryContainer,
			}}
			alignItems="center"
			justifyContent="center"
		>
			<Text
				variant="labelMedium"
				style={{
					color: labelColor,
					fontSize: Math.round(size * 0.375),
					fontWeight: "500",
				}}
			>
				{formatLabel(number)}
			</Text>
			{dotColor ? (
				<Dot
					color={dotColor}
					size={size}
					ringColor={theme.colors.primaryContainer}
				/>
			) : null}
		</Box>
	);
};

const QuestionMapItem = ({
	number,
	state,
	size = 32,
}: QuestionMapItemProps) => {
	const theme = useAppTheme();
	const circle = (
		<Circle state={state} number={number} size={size} theme={theme} />
	);

	if (state !== "current") {
		return circle;
	}

	const wrapperSize = size + 6;
	return (
		<Box
			style={{
				width: wrapperSize,
				height: wrapperSize,
				borderRadius: wrapperSize / 2,
				backgroundColor: theme.colors.primaryContainer,
			}}
			alignItems="center"
			justifyContent="center"
		>
			{circle}
		</Box>
	);
};

export default QuestionMapItem;
